import React, { useState, useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import withStyles from '@material-ui/core/styles/withStyles';
import FormHelperText from '@material-ui/core/FormHelperText';
import Button from '@material-ui/core/Button';
import Page from 'Components/Page';
import { useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import * as actions from 'Store/actions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import FormTextField from 'Components/FormTextField';
import Loading from 'Components/Loading';
import * as Regex from '../regex';
import { isLength } from 'lodash';
import { isEmpty } from 'lodash';
import Box from '@material-ui/core/Box';
import * as UserHelpers from 'Helpers/user';
import Link from '@material-ui/core/Link';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControlLabel';

const pageStyles = theme => ({
  pageWrapper: {
    paddingLeft: '3%',
    paddingRight: '3%',
  },
  addEditForm: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionalMessage: {
    fontSize: 18,
  },
  button: {
    alignSelf: 'flex-start',
    marginTop: '90px',
  },
  photoContainer: {
    marginBottom: '40px',
  },
  link: {
    textDecoration: 'underline',
    cursor: 'pointer',
    color: 'inherit',
  },
  login: {
    marginTop: '40px',
  },
  proceedButton: {
    color: '#fff',
  },
});

function Register({
  classes,
  checkoutReset,
  urlCode,
  setActiveModal,
  user,
  logoutUser,
  updateItem,
  registerWithFirebase,
}) {
  let _user = user || {};

  const { register, handleSubmit, errors, getValues, setValue } = useForm({
    defaultValues: {
      code: urlCode || '',
    },
  });

  useEffect(() => {
    setValue('contactEmail', user?.email);
    setValue('phone', user?.phone);
  }, [setValue, user]);

  let { password, confirmPassword } = getValues();

  const [loading, setLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [displayCode, setDisplayCode] = useState();
  const [isError, setIsError] = useState(false);

  //Todo: this needs to be in useEffect maybe, doesn't seem to be working
  checkoutReset();

  const openLoginModal = () => {
    setActiveModal('login');
  };

  const onSubmit = async data => {
    setLoading(true);

    //<!-- Event snippet for Website sale conversion page -->
    let { firstName, lastName, email, password, phone, message, code, contactEmail } = data;
    let userId;

    // if not logged in then try to register
    // if logged in then submit
    if (!isEmpty(user)) {
      firstName = user.firstName;
      lastName = user.lastName;
      userId = user.uid;
      email = user.email;
    } else {
      try {
        const registrationRequest = await registerWithFirebase({
          email,
          password,
          firstName,
          lastName,
          phone,
        });

        userId = registrationRequest.payload.uid;
      } catch (err) {
        console.warn('Registration error: ', err);
        alert(err);
        return;
      }
    }

    const _item = {
      // all item information is in info because we
      // have cart information and item information in the same
      // reducer. They should probably split at some point -NJ
      info: {
        itemId: code,
        ownerId: userId,
        claimed: true,
        email: contactEmail || email,
        phone,
        message,
      },
    };

    try {
      await updateItem(_item);
    } catch (err) {
      setLoading(false);
      setIsError(true);
      setDisplayCode(code);
      return;
    }

    setDisplayCode(code);
    setLoading(false);
    setIsComplete(true);
  };

  const tosWindow = () => {
    window.open(`${window.location.origin}/TOS?hasNav=false`, 'tos', 'width=500,height=800');
  };

  function getPhoneHelpertext() {
    if (errors.phone) {
      return errors.phone.message;
    }

    return `If provided text messages will be sent here regarding lost items. `;
  }

  const getCodeHelperText = () => {
    if (errors.code) {
      return errors.code.message;
    }

    return "Please enter the 6 digit code. It's case sensitive.";
  };

  const getEmailErrorMessage = () => {
    if (errors.email) {
      return errors.email.type === 'isTaken' ? (
        <>
          This email is taken. Please{' '}
          <span className={classes.link} onClick={openLoginModal}>
            (Log in)
          </span>{' '}
          or use another.
        </>
      ) : (
        errors.email.message
      );
    }

    return false;
  };

  const renderUserInformationFields = () => {
    return (
      <div>
        <Typography variant="h5" className={classes.login}>
          Already have an account?{' '}
          <span className={classes.link} onClick={openLoginModal}>
            (Log in here.)
          </span>
        </Typography>

        <FormTextField
          label="First Name"
          name="firstName"
          required
          error={!!errors.firstName}
          helperText={errors.firstName && errors.firstName.message}
          inputRef={register({ required: 'Provide your first name' })}
          placeholder="Enter First Name"
          style={{ marginRight: 20 }}
        />

        <FormTextField
          label="Last Name"
          name="lastName"
          required
          error={!!errors.lastName}
          helperText={errors.lastName && errors.lastName.message}
          inputRef={register({ required: 'Provide your last name' })}
          placeholder="Enter Last Name"
        />

        <FormTextField
          fullWidth
          label="Email"
          name="email"
          required
          error={!!errors.email}
          helperText={getEmailErrorMessage()}
          helperTextComponent="div"
          inputRef={register({
            required: 'Provide the email',
            pattern: {
              value: Regex.email,
              message: 'Provide a valid email address',
            },
            validate: {
              isTaken: async value => {
                //const isEmailTaken = await debouncedIsEmailTakenMethod(value);
                const isEmailTaken = await UserHelpers.isEmailTaken(value);
                if (isEmailTaken) {
                  return 'Email is taken';
                }
              },
            },
          })}
          placeholder="Enter Email"
        />

        <Box display="flex">
          <FormTextField
            label="Password"
            name="password"
            type="password"
            required
            error={!!errors.password}
            helperText={errors.password && errors.password.message}
            inputRef={register({
              required: 'Provide your password',
              minLength: {
                value: 8,
                message: 'Password must be longer than 8 characters',
              },
              validate: value => {
                if (confirmPassword && value !== confirmPassword) {
                  return 'Passwords need to match';
                }
              },
            })}
            placeholder="Enter Password"
            style={{ marginRight: 20 }}
          />

          <FormTextField
            label="Confirm"
            name="confirmPassword"
            type="password"
            required
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword && errors.confirmPassword.message}
            inputRef={register({
              required: 'Confirm your password',
              validate: value => {
                if (password && value !== password) {
                  return 'Passwords need to match';
                }
              },
            })}
            placeholder="Enter Password"
          />
        </Box>

        <Box display="flex">
          <FormControl
            control={
              <Checkbox
                name="tosCheckbox"
                className={classes.productCheckbox}
                inputRef={register({
                  required: 'Please agree to the terms of service',
                })}
                required={true}
              />
            }
            label={
              <p>
                I have read and agree to the{' '}
                <Link onClick={tosWindow} target={'tos'}>
                  terms of service
                </Link>
                .
              </p>
            }
            color="primary"
          >
            {errors.tosCheckbox && <FormHelperText>'Please agree to the terms of service'</FormHelperText>}
          </FormControl>
        </Box>
      </div>
    );
  };

  if (isComplete) {
    return (
      <Page wrapperStyles={classes.pageWrapper}>
        <div style={{ marginTop: 50 }}>
          <h1>Success!</h1>
          <p>Your code {displayCode}, is now registered!</p>
          <p>Visit the dashboard to edit your tag information or register another.</p>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.proceedButton}
            component={Link}
            href="/dashboard"
            style={{ marginTop: 30, textDecoration: 'none' }}
          >
            Proceed to Dashboard
          </Button>
          <div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.proceedButton}
              component={Link}
              href="/register"
              style={{ marginTop: 30, textDecoration: 'none' }}
            >
              Register Another Tag
            </Button>
          </div>
        </div>
      </Page>
    );
  }

  if (isError) {
    return (
      <Page wrapperStyles={classes.pageWrapper}>
        <div style={{ marginTop: 50 }}>
          <h1>Incorrect Code or Already Registered</h1>
          <p>The code {displayCode}, does not exist or has been registered already by another user.</p>
          <p>If this is a new tag or seems incorrect plase contact support@ownerfy.com.</p>
          <div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.proceedButton}
              component={Link}
              href="/register"
              style={{ marginTop: 30, textDecoration: 'none' }}
            >
              Start Over
            </Button>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page wrapperStyles={classes.pageWrapper}>
      {loading ? (
        <Loading
          type={'bubbles'}
          color={'#ccc'}
          height={'80px'}
          width={'80px'}
          containerHeight={'40vh'}
          containerWidth={'100%'}
          message={'Updating...'}
        />
      ) : (
        <>
          <Typography variant="h1">Register Tag</Typography>
          <FormHelperText>
            Your email and phone number will always be kept private and we will relay messages to you.
          </FormHelperText>
          <form onSubmit={handleSubmit(onSubmit).bind(this)} className={classes.addEditForm}>
            <FormTextField
              fullWidth
              label="Code on Tag"
              name="code"
              inputProps={{
                maxLength: 6,
              }}
              required
              error={!!errors.code}
              helperText={getCodeHelperText()}
              inputRef={register({
                required: 'Please enter 6 digit code',
                pattern: {
                  value: /^[A-Z0-9]{6}$/i,
                  message: 'Code must be 6 digits',
                },
              })}
              placeholder="Code on Tag"
            />

            {isEmpty(user) ? (
              renderUserInformationFields()
            ) : (
              <>
                <Typography variant="h6">
                  Logged in as {_user.firstName}{' '}
                  <span className={classes.link} onClick={logoutUser}>
                    (Log out)
                  </span>
                </Typography>
                <FormTextField
                  fullWidth
                  label="Contact Email"
                  name="contactEmail"
                  required
                  error={!!errors.email}
                  helperText={errors.email && errors.email.message}
                  inputRef={register({
                    required: 'Provide the contact email',
                    pattern: {
                      value: Regex.email,
                      message: 'Provide a valid email address',
                    },
                  })}
                  placeholder="Enter Contact Email"
                />
              </>
            )}

            <NumberFormat
              customInput={FormTextField}
              format="###-###-####"
              mask="_"
              fullWidth
              label="Contact Phone"
              name="phone"
              value={isEmpty(user) ? '' : user.phone}
              error={!!errors.phone}
              helperText={getPhoneHelpertext()}
              inputRef={register}
              placeholder="Enter Phone Number"
            />

            <FormTextField
              fullWidth
              label="Message"
              name="message"
              error={!!errors.message}
              helperText={errors.message ? errors.message.message : 'Add an optional message to display.'}
              inputRef={register({
                required: false,
              })}
              placeholder="Enter Message"
              multiline
            />

            <Button type="submit" variant="contained" color="primary" className={classes.button}>
              Submit
            </Button>

            <FormHelperText>*Required</FormHelperText>
          </form>
        </>
      )}
    </Page>
  );
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      registerWithFirebase: actions.registerWithFirebase,
      checkoutReset: actions.checkoutReset,
      setActiveModal: actions.setActiveModal,
      logoutUser: actions.logoutUser,
      updateItem: actions.updateItem,
    },
    dispatch,
  );
}

function mapStateToProps({ user }, ownProps) {
  return {
    user,
    urlCode: ownProps.match.params.code,
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(pageStyles)(Register)));
